import { z } from 'zod';

/**
 * Validates import.meta.env at module load so a missing/misconfigured env var fails fast at
 * startup instead of surfacing as a confusing runtime network error later.
 */
const envSchema = z.object({
  VITE_GATEWAY_URL: z.string().url(),
  VITE_GRAPHQL_URL: z.string().url(),
  VITE_APP_ENV: z.enum(['development', 'qa', 'production']),
  VITE_APP_NAME: z.string().min(1),
  /**
   * The browser-facing origin of THIS deployment, used to build absolute canonical/OG URLs.
   *
   * Not derivable from `window.location.origin`: a canonical must name one address, and the same
   * bundle is reachable from more than one host (a preview host, an IP, the bare domain vs www).
   * Deriving it would make each host declare itself canonical, which is the duplicate-content
   * problem canonicals exist to solve. The backend's `EmailNotifications:WebBaseUrl` is the same
   * value on the server side — keep the two in step.
   */
  VITE_PUBLIC_SITE_URL: z.string().url(),
  VITE_SESSION_IDLE_TIMEOUT_MINUTES: z.coerce.number().positive(),
  // PLACEHOLDER — nothing reads this yet. No telemetry SDK is wired in the SPA (see ErrorBoundary),
  // and OpenTelemetry/APM is a deliberate post-launch deferral, so this stays optional and empty. It is
  // kept rather than deleted because it is the seam that work will use; treat a value here as having no
  // effect until a client is actually initialised from it.
  VITE_APPINSIGHTS_CONNECTION_STRING: z.string().optional().default(''),
  // Optional: unset in local dev disables the corresponding SocialLoginButtons entry rather than
  // failing startup — no Google/LinkedIn app is registered for this environment yet.
  VITE_GOOGLE_CLIENT_ID: z.string().optional().default(''),
});

const parsed = envSchema.safeParse(import.meta.env);

if (!parsed.success) {
  console.error('Invalid environment configuration:', parsed.error.flatten().fieldErrors);
  throw new Error('Invalid environment configuration — check .env against .env.example');
}

export const env = {
  gatewayUrl: parsed.data.VITE_GATEWAY_URL,
  graphqlUrl: parsed.data.VITE_GRAPHQL_URL,
  appEnv: parsed.data.VITE_APP_ENV,
  appName: parsed.data.VITE_APP_NAME,
  // Trailing slash trimmed once here so every caller can concatenate a leading-slash path.
  publicSiteUrl: parsed.data.VITE_PUBLIC_SITE_URL.replace(/\/+$/, ''),
  sessionIdleTimeoutMinutes: parsed.data.VITE_SESSION_IDLE_TIMEOUT_MINUTES,
  appInsightsConnectionString: parsed.data.VITE_APPINSIGHTS_CONNECTION_STRING,
  isProduction: parsed.data.VITE_APP_ENV === 'production',
  googleClientId: parsed.data.VITE_GOOGLE_CLIENT_ID,
} as const;
